<template>
  <div class="tier-step tier-conditions-step">
    <h2 class="tier-step-title">{{ is_edit ? 'Edit conditions' : 'Set conditions' }}</h2>
    <p class="tier-step-desc">
      Rule: Each condition can only be applied once per tier. If a condition is used on a higher tier, it must have a
      greater value than the lower tier. Additionally, the order of tiers can be influenced by these rules. You can have
      a maximum of {{ max_tiers }} tiers, including drafts and default tiers.
    </p>
    <p class="tier-step-hint">Rank by highest to lowest</p>

    <div class="tier-list">
      <div
        :class="['tier-item', index === ranked_tiers.length - 1 && 'is-last']"
        v-for="(row, index) in ranked_tiers"
        :key="row.tier.key"
      >
        <div class="tier-rank">
          <!-- the number is the position from the top, matching the define step -->
          <span class="tier-rank-num">{{ index + 1 }}</span>
        </div>

        <div class="tier-card">
          <div class="tier-chip-row">
            <TierBadge :tier="row.tier" :size="32" />
            <span class="tier-chip-default" v-if="isStartingTier(row.tier)">(Default)</span>
          </div>

          <div class="tier-conditions-box">
            <div class="tier-conditions-head">
              <div class="tier-conditions-title">
                Conditions<span class="tier-required">*</span>
                <tippy
                  class="tier-conditions-help"
                  content="Creators reach this tier once every condition below is met"
                >
                  <SvgIcon name="question-3" />
                </tippy>
              </div>
              <!-- nothing to add on the default tier, everyone lands there without qualifying -->
              <i
                class="tier-condition-add"
                v-if="!isStartingTier(row.tier)"
                title="Add condition"
                @click="addCondition(row.index, row.tier)"
              >
                <SvgIcon name="plus" />
              </i>
            </div>

            <!-- the tier's own description shows as a tooltip on its chip, not here -->
            <p class="tier-conditions-desc" v-if="isStartingTier(row.tier)">
              All new creators start here automatically. No qualifying conditions are required.
            </p>

            <template v-else>
              <p class="tier-conditions-desc">Creators are require to complete following tasks</p>

              <div class="tier-condition" v-for="(condition, ci) in row.tier.conditions" :key="condition.key">
                <div class="tier-condition-row">
                  <span class="tier-condition-join">{{ ci === 0 ? 'Where' : 'And' }}</span>

                  <el-select
                    :class="[rowError(row.tier, condition, ci) && !condition.field && 'err-input']"
                    v-model="condition.field"
                    placeholder="Select a condition"
                    @change="onFieldChange(row.index, condition)"
                  >
                    <!-- already used by another row in this tier, or not available to the
                         link-only accounts this tier is narrowed to -->
                    <el-option
                      v-for="field in fields"
                      :key="field.value"
                      :label="field.label"
                      :value="field.value"
                      :disabled="
                        isFieldTaken(row.tier, condition, field.value) || isFieldBlocked(row.tier, field.value)
                      "
                    />
                  </el-select>

                  <el-select
                    :class="[rowError(row.tier, condition, ci) && !condition.operator && 'err-input']"
                    v-model="condition.operator"
                    placeholder="---"
                    :disabled="!condition.field"
                    @change="onShapeEdit(row.index)"
                  >
                    <el-option
                      v-for="op in operatorsFor(condition.field)"
                      :key="op"
                      :label="operatorLabel(op)"
                      :value="op"
                    />
                  </el-select>

                  <el-select
                    v-if="fieldUnit(condition.field) === units.BOOLEAN"
                    :class="[rowError(row.tier, condition, ci) && 'err-input']"
                    v-model="condition.value"
                    placeholder="---"
                    @change="onValueEdit(row.index)"
                  >
                    <el-option label="True" :value="true" />
                    <el-option label="False" :value="false" />
                  </el-select>
                  <el-select
                    v-else-if="fieldUnit(condition.field) === units.ENUM"
                    :class="[rowError(row.tier, condition, ci) && 'err-input']"
                    v-model="condition.value"
                    placeholder="Select"
                    @change="onValueEdit(row.index)"
                  >
                    <el-option
                      v-for="option in fieldOptions(condition.field)"
                      :key="option.value"
                      :label="option.label"
                      :value="option.value"
                    />
                  </el-select>
                  <el-input
                    v-else
                    :class="[valueHasError(row.index, row.tier, condition, ci) && 'err-input']"
                    v-model="condition.value"
                    :placeholder="isMoneyField(condition.field) ? '' : 'Add Value'"
                    :disabled="!condition.field"
                    @input="onValueInput(row.index, condition, $event)"
                  >
                    <!-- the slot is always provided so every value input keeps the same padding,
                         only the currency metric actually draws the sign -->
                    <template #prefix>
                      <span class="tier-condition-money" v-if="isMoneyField(condition.field)">$</span>
                    </template>
                  </el-input>

                  <i
                    class="tier-condition-del"
                    title="Remove condition"
                    @click="removeCondition(row.index, row.tier, ci)"
                    v-if="ci !== 0"
                  >
                    <SvgIcon name="delete2" />
                  </i>
                </div>

                <p class="tier-condition-error" v-if="issue(row.index, ci)">{{ issue(row.index, ci) }}</p>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ElInput, ElSelect, ElOption } from 'element-plus';
import SvgIcon from '@/components/ui/SvgIcon.vue';
import TierBadge from './TierBadge.vue';
import {
  conditionIssue,
  conditionFieldsFor,
  copyConditionShape,
  createCondition,
  defaultValueFor,
  fieldOptions,
  fieldUnit,
  isConditionBlank,
  isConditionComplete,
  isFieldBlocked,
  isFieldTaken,
  isMirrorIntact,
  isStartingTier,
  MAX_TIERS,
  operatorLabel,
  operatorsFor,
  UNIT,
} from './tierModel';

export default {
  name: 'TierConditionsStep',

  components: { ElInput, ElSelect, ElOption, SvgIcon, TierBadge },

  props: {
    tiers: {
      type: Array,
      default: () => [],
    },
    is_edit: {
      type: Boolean,
      default: false,
    },
    // flipped on by the dialog when Save hits an incomplete tier
    show_errors: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      units: UNIT,
      max_tiers: MAX_TIERS,
      // this step is rebuilt on every Next, so the check runs again each time it is entered
      autofill_allowed: isMirrorIntact(this.tiers),
    };
  },

  computed: {
    // the metric list depends on the merchant, so it cannot be a static data field
    fields() {
      return conditionFieldsFor(this.$store);
    },

    // `index` addresses the row for the cross tier checks. the displayed number is the
    // position, deliberately not tier.rank, which counts the other way round.
    ranked_tiers() {
      return this.tiers.map((tier, index) => ({ tier, index }));
    },
  },

  methods: {
    isStartingTier,
    fieldOptions,
    fieldUnit,

    // the amount is money, so the input shows a $ while the model keeps the bare number
    isMoneyField(field) {
      return fieldUnit(field) === UNIT.CURRENCY;
    },
    isFieldBlocked,
    isFieldTaken,
    operatorLabel,
    operatorsFor,

    // rule breaches show as soon as they happen, they are not gated on a Save attempt
    issue(tier_index, condition_index) {
      return conditionIssue(this.tiers, tier_index, condition_index);
    },

    // each empty control lights up on its own, so the red clears as the row gets filled in
    rowError(tier, condition, index) {
      if (!this.show_errors || isConditionComplete(condition)) return false;
      // a row that was started but left unfinished is always wrong
      if (!isConditionBlank(condition)) return true;
      // otherwise the tier has no usable rule at all, so ask for the first row
      return index === 0 && !tier.conditions.some(isConditionComplete);
    },

    valueHasError(tier_index, tier, condition, index) {
      if (this.issue(tier_index, index)) return true;
      return this.rowError(tier, condition, index) && condition.value === '';
    },

    // count metrics take whole numbers, currency allows decimals
    onValueInput(tier_index, condition, value) {
      const pattern = fieldUnit(condition.field) === UNIT.CURRENCY ? /[^\d.]/g : /[^\d]/g;
      const cleaned = String(value).replace(pattern, '');
      if (cleaned !== value) condition.value = cleaned;
      this.onValueEdit(tier_index);
    },

    addCondition(tier_index, tier) {
      tier.conditions.push(createCondition());
      this.onShapeEdit(tier_index);
    },

    removeCondition(tier_index, tier, index) {
      tier.conditions.splice(index, 1);
      if (tier.conditions.length === 0) tier.conditions.push(createCondition());
      this.onShapeEdit(tier_index);
    },

    onFieldChange(tier_index, condition) {
      const operators = operatorsFor(condition.field);
      if (!operators.includes(condition.operator)) condition.operator = operators[0];
      condition.value = defaultValueFor(condition.field);
      this.onShapeEdit(tier_index);
    },

    // adding, removing or repointing a row on the top tier mirrors down while nothing else is filled in
    onShapeEdit(tier_index) {
      if (tier_index !== 0) {
        this.autofill_allowed = false;
        return;
      }
      if (this.autofill_allowed) this.mirrorTopTier();
    },

    // values are never mirrored, so editing one only matters as a signal to stop mirroring
    onValueEdit(tier_index) {
      if (tier_index !== 0) this.autofill_allowed = false;
    },

    mirrorTopTier() {
      const source = this.tiers[0];
      if (!source || isStartingTier(source)) return;

      this.tiers.forEach((tier, index) => {
        if (index === 0 || isStartingTier(tier)) return;
        tier.conditions = source.conditions.map(copyConditionShape);
      });
    },
  },
};
</script>
<style lang="scss">
.tier-conditions-step {
  // the chip carries the icon and colors picked on step one
  .tier-chip-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }

  .tier-chip-default {
    color: var(--black-50);
  }

  // the chip sits outside the bordered box, so the row itself carries no frame
  // the chip sits above the bordered box, so the row itself carries no frame
  .tier-card {
    padding: 0;
    border: none;
    background: none;
  }

  .tier-conditions-box {
    padding: 16px;
    border: 1px solid var(--black-20);
    border-radius: 8px;
  }

  .tier-conditions-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 8px;
  }

  .tier-conditions-title {
    display: flex;
    align-items: center;
    font: var(--font-med-20);
    color: var(--black-90);
  }

  .tier-conditions-help {
    display: flex;
    margin-left: 8px;
    color: var(--black-40);
    cursor: pointer;

    &:hover {
      color: var(--black-90);
    }

    svg {
      width: 14px;
      height: 14px;
    }
  }

  .tier-condition-add {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid var(--black-20);
    border-radius: 6px;
    color: var(--black-90);
    cursor: pointer;

    svg {
      width: 14px;
      height: 14px;
    }

    &:hover {
      border-color: var(--black-90);
    }
  }

  .tier-conditions-desc {
    color: var(--black-90);
    margin: 4px 0 16px;

    // the default tier ends on this line, so it should not pad the bottom of the box
    &:last-child {
      margin-bottom: 0;
    }
  }

  .tier-condition {
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  // fixed label gutter, the rest splits between field and value at roughly the design's ratio
  .tier-condition-row {
    display: grid;
    grid-template-columns: 80px minmax(200px, 2.2fr) 110px minmax(130px, 1fr) 20px;
    gap: 12px;
    align-items: center;
  }

  // lines up under the metric select: 80px label column plus the 12px gap
  .tier-condition-error {
    margin: 4px 0 0 92px;
    font-size: 12px;
    color: var(--alert);
  }

  .tier-condition-join {
    font: var(--font-med-16);
    color: var(--black-90);
  }

  .el-select__wrapper {
    min-height: 40px;
    border-radius: 6px;
  }

  .tier-condition-money {
    color: var(--black-90);
    position: relative;
    top: -1px;

    .el-input__prefix-inner > :last-child {
      margin-right: 0;
    }
  }

  .tier-condition-del {
    display: flex;
    color: var(--black-75);
    cursor: pointer;

    svg {
      width: 18px;
      height: 18px;
    }

    &:hover {
      color: var(--black-90);
    }
  }
}
</style>
